<template>
    <MainCard>
        <rb-loader v-model="loader"></rb-loader>
        <div class="assistent-configuration w-100">
            <div class="assistent-configuration-title w-100 d-flex justify-space-between align-center">
                <div class="d-flex align-center ga-6">
                    <v-btn class="px-6 py-5 d-flex" @click="handleExitClick" color="neutral-35" variant="text"
                        icon="mdi-arrow-left"></v-btn>
                    <h1>{{ pageTitle }}</h1>
                    <rb-switch v-if="isEditing" v-model="assistentStatus"
                        :label="assistentStatus ? 'Ativado' : 'Desativado'" />
                </div>
                <div v-if="isEditing" class="latest-log-register">
                    <p>Última alteração: {{ formattedDate }}</p>
                </div>
            </div>
            <div class="assistent-configuration-content">
                <v-card variant="text">
                    <v-tabs :color="getTabColor(tab)" class="navigation-tab" v-model="tab">
                        <v-tab class="navigation-tab-item" v-for="item in items" :key="item.value" :value="item.value"
                            :color="getTabItemColor(item.value)">
                            {{ item.title }}
                            <template v-if="item.icon" v-slot:append>
                                <v-icon color="alert-system-dark" icon="mdi-alert-circle">
                                </v-icon>
                            </template>
                        </v-tab>
                    </v-tabs>
                    <v-divider class="w-100"></v-divider>

                    <v-card-text style="padding: 32px;">
                        <v-window v-model="tab">
                            <v-window-item eager value="goal">
                                <v-form :ref="el => forms.goal.ref = el" validate-on="input"
                                    v-model="forms.goal.model">
                                    <TabObjetivos v-model="goal" :related-links="helpLinks.objetivo"
                                        :input-rules="inputRules">
                                    </TabObjetivos>
                                </v-form>
                            </v-window-item>

                            <v-window-item eager value="behavior">
                                <v-form :ref="el => forms.behavior.ref = el" validate-on="input"
                                    v-model="forms.behavior.model">
                                    <TabComportamentos v-model="behavior"
                                        :related-links="helpLinks.comportamento" :input-rules="inputRules">
                                    </TabComportamentos>
                                </v-form>
                            </v-window-item>

                            <v-window-item value="references">
                                <v-form :ref="el => forms.references.ref = el" validate-on="input"
                                    v-model="forms.references.model">
                                    <TabReferencias v-model="references" :related-links="helpLinks.referencias"
                                        :input-rules="inputRules">
                                    </TabReferencias>
                                </v-form>
                            </v-window-item>

                            <v-window-item eager value="integration">
                                <v-form :ref="el => forms.integration.ref = el" validate-on="input"
                                    v-model="forms.integration.model">
                                    <TabIntegracao v-model="integration" :related-links="helpLinks.integracao"
                                        :input-rules="inputRules">
                                    </TabIntegracao>
                                </v-form>
                            </v-window-item>

                            <v-window-item value="interests">
                                <v-form :ref="el => forms.interests.ref = el" validate-on="input"
                                    v-model="forms.interests.model">
                                    <TabInteresses v-model="interests" :input-rules="inputRules">
                                    </TabInteresses>
                                </v-form>
                            </v-window-item>

                            <v-window-item value="fields">
                                <v-form :ref="el => forms.fields.ref = el" v-model="forms.fields.model">
                                    <TabCampos v-model="fields" :input-rules="inputRules"></TabCampos>
                                </v-form>
                            </v-window-item>

                            <v-window-item v-if="lgpd.lgpdActive || lgpd.lgpdDefinitiveActive" value="lgpd">
                                <v-form :ref="el => forms.lgpd.ref = el" v-model="forms.lgpd.model">
                                    <TabLgpd v-model="lgpd" :input-rules="inputRules" :assistant-name="goal.name" :assistant-image="goal.image" :is-editing="isEditing"></TabLgpd>
                                </v-form>
                            </v-window-item>
                        </v-window>
                    </v-card-text>
                </v-card>
            </div>

            <div class="navigation-buttons">
                <v-divider class="w-100"></v-divider>
                <div class="buttons-container w-100 d-flex justify-space-between">
                    <v-btn class="px-6 py-5 d-flex" rounded="xl" v-if="tab !== 'goal'" @click="previousTab" variant="outlined"
                        color="neutral-35">Anterior</v-btn>
                    <div class="action-buttons-container d-flex ga-4">
                        <v-btn class="px-6 py-5 d-flex" rounded="xl" variant="outlined" color="neutral-35"
                            @click="handleExitClick">Cancelar</v-btn>
                        <v-btn class="px-6 py-5 d-flex" rounded="xl" :disabled="tab == items[items.length - 1].value && !saveButton" color="primary-60"
                            @click="tab == items[items.length - 1].value ? saveAssistent() : nextTab()">{{ tab ==
                                items[items.length - 1].value ? 'Salvar' : 'Próximo' }}</v-btn>
                    </div>
                </div>
            </div>
            <rb-popup v-model="exitPopUp" @confirm="confirmExit" confirm-text="Sair" confirm-color="error-40">
                <template #title>
                    Aviso
                </template>
                <template #message>
                    Você tem certeza que quer sair? Seu progresso será perdido.
                </template>
            </rb-popup>
        </div>
    </MainCard>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useSnackbarStore } from '@/ai_studio/stores/snackbar';
import { useRoute, useRouter } from 'vue-router';
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';
import TabObjetivos from './components/TabObjetivos.vue';
import TabComportamentos from './components/TabComportamentos.vue';
import TabReferencias from './components/TabReferencias.vue';
import TabIntegracao from './components/TabIntegracao.vue';
import RbPopup from '@/ai_studio/components/common/RBPopup.vue';
import MainCard from '@/ai_studio/components/layout/MainCard.vue';
import TabInteresses from './components/TabInteresses.vue';
import TabCampos from './components/TabCampos.vue';
import TabLgpd from './components/TabLgpd.vue';
import { fetchProperties, contextData, request } from '@/ai_studio/utils/fetchData';
const snackbar = useSnackbarStore();
const route = useRoute();
const router = useRouter();
const isEditing = computed(() => route.path !== '/assistentes/create');
const tab = ref('goal');
const loader = ref(isEditing.value ? true : false);
const assistentKey = route.params.key;
const originalAssistentStatus = ref(null);
const assistentStatus = ref(null);
const originalImageUrl = ref(null);
const loadedData = ref(null);
const latestLog = ref(null);
const forms = ref({
    goal: { ref: null, model: null, loaded: null },
    behavior: { ref: null, model: null, loaded: null },
    references: { ref: null, model: null, loaded: null },
    integration: { ref: null, model: null, loaded: null },
    interests: { ref: null, model: null, loaded: null },
    fields: { ref: null, model: null, loaded: null },
    lgpd: { ref: null, model: null, loaded: null },
})

const LGPD_TYPES = {
    1: 'LEGAL_BASIS_AND_SIGNATURES',
    2: 'SIGNATURES_AND_LEGITIMATE_INTEREST_CONSENT',
    3: 'LEGITIMATE_INTEREST_CONSENT',
};

const hasText = (html) => !!html?.replace(/<[^>]*>/g, '').trim();

const lgpdValid = computed(() => {
    const { lgpdTypeId, lgpdDefinitiveActive, legalBasisConfig, signaturesConfig } = lgpd.value;
    if (!lgpdTypeId) {
        return !lgpdDefinitiveActive;
    }
    
    const type = LGPD_TYPES[lgpdTypeId];
    const hasSignatures = type === 'LEGAL_BASIS_AND_SIGNATURES' || type === 'SIGNATURES_AND_LEGITIMATE_INTEREST_CONSENT';

    if (type === 'LEGAL_BASIS_AND_SIGNATURES' && (!legalBasisConfig.crmLegalBasisId || !hasText(legalBasisConfig.acceptanceKeyword))) {
        return false;
    }

    if (!hasText(legalBasisConfig.privacyText) || !hasText(legalBasisConfig.acceptanceText)) {
        return false;
    }

    if (type === 'LEGAL_BASIS_AND_SIGNATURES' && !hasText(legalBasisConfig.rejectionKeyword)) {
        return false;
    }

    if (hasSignatures && !signaturesConfig.every(signature =>
        signature.crmSignatureId && hasText(signature.acceptanceText) && hasText(signature.acceptanceKeyword) && hasText(signature.rejectionKeyword)
    )) return false;

    return true;
});

const saveButton = computed(() => {
    return Boolean(forms.value.goal.model && forms.value.behavior.model && forms.value.integration.model && (!interests.value.outOfSync || interests.value.courseOffers.length === 0) && interests.value.courseOffers.length <= 1000 && lgpdValid.value);
});

const formattedDate = computed(() => {
    if (!latestLog.value) return '--';
    const date = new Date(latestLog.value);
    return isNaN(date) ? '--' : date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
});

const checkTabLoaded = (tabName, checkAll=false) => {
    if (!forms.value) return;
    if (!tabName && checkAll) for (let key in forms.value) forms.value[key].loaded = true;
    if (tabName && forms.value[tabName]) forms.value[tabName].loaded = true
}

const isTabInvalid = (tabName) => {
    return isCheckTabLoaded(tabName) && forms.value[tabName]?.model === false;
};

const isCheckTabLoaded = (tabName) => {
    if (!forms.value || !tabName || forms.value[tabName]) return true;
    return forms.value[tabName]?.loaded
}

const exitPopUp = ref(false);
const regexUrl = new RegExp('^(https?:\\/\\/)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\\/[-a-zA-Z0-9()@:%_+.~#?&//=]*)?$', 'i');


const helpLinks = {
    objetivo: [
        { content: 'Como configurar os objetivo do seu Assistente Rubeus', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302428278413-Como-preencher-a-etapa-Objetivo-no-cadastro-de-um-novo-assistente' },
        { content: 'Passo a Passo: Como configurar seu Rubeus Assistente', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302316230669-Assistente-IA-Rubeus-Tudo-o-que-você-precisa-saber-para-configurar-o-seu' },
    ],
    comportamento: [
        { content: 'Como configurar o comportamento do seu Assistente Rubeus', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302559165325-Como-preencher-a-etapa-Comportamento-no-cadastro-de-um-novo-assistente' },
        { content: 'Passo a Passo: Como configurar seu Rubeus Assistente', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302316230669-Assistente-IA-Rubeus-Tudo-o-que-você-precisa-saber-para-configurar-o-seu' },
        { content: 'Boas práticas de Engenharia de Prompt', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/40332537613069-Rubeus-Assistente-Boas-práticas-de-Engenharia-de-Prompt'}
    ],
    referencias: [
        { content: 'Como escolher boas referências  para sua AI', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302717333901-Como-preencher-a-etapa-Referências-no-cadastro-de-um-novo-assistente' },
        { content: 'Passo a Passo: Como configurar seu Rubeus Assistente', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302316230669-Assistente-IA-Rubeus-Tudo-o-que-você-precisa-saber-para-configurar-o-seu' }
    ],
    integracao: [
        { content: 'Como realizar a integração de seu rubeus assistente', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302814556173-Como-preencher-a-etapa-Integração-no-cadastro-de-um-novo-assistente' },
        { content: 'Passo a Passo: Como configurar seu Rubeus Assistente', link: 'https://crmrubeus.zendesk.com/hc/pt-br/articles/36302316230669-Assistente-IA-Rubeus-Tudo-o-que-você-precisa-saber-para-configurar-o-seu' }
    ]
}

const confirmExit = () => {
    router.push({ path: '/assistentes' })
}

const goal = ref({
    name: '',
    institute: '',
    color: '',
    image: null,
    speciality: '',
    values: '',
});

const behavior = ref({
    checkpointStatus: false,
    presentation: '',
    instructions: '',
    endChat: '',
    commercial: '',
    errorMessage: '',
});

const references = ref({
    assistentKey: assistentKey,
    referencesLinks: [],
    referencesArchives: [],
});

const integration = ref({
    channelSelected: null,
    channelList: [],
    allModels: [],
    stableModels: [],
    model: null,
    fallbackModel: null,
    loading: isEditing.value ? true : false,
    modelStatus:  isEditing.value ? null : true,
});

const interests = ref({
    conditions: [],
    changed: false,
    stored: [],
    saved: [],
    outOfSync: false,
    loading: isEditing.value ? true : false,
    courseOffers: [],
})

const fields = ref({
    properties: [],
    selectedProperties: [],
    loading: isEditing.value ? true : false,
})

const lgpd = ref({
    lgpdActive: false,
    lgpdDefinitiveActive: false,
    lgpdTypeOptions: [],
    crmSignatureOptions: [],
    crmLegalBasisOptions: [],
    lgpdTypeId: null,
    selectedCrmSignatureIds: [],
    signaturesConfig: [{
        crmSignatureId: null,
        acceptanceText: '',
        acceptanceKeyword: '',
        rejectionKeyword: '',
        required: true
    }],
    legalBasisConfig: {
        crmLegalBasisId: null,
        privacyText: '',
        acceptanceText: '',
        acceptanceKeyword: '',
        rejectionKeyword: ''
    }
})


const initialSnapshot = ref('');
const hasChanges = ref(false);
const makeSnapshot = () => JSON.stringify({
  assistentStatus: assistentStatus.value,
  goal: goal.value,
  behavior: behavior.value,
  references: references.value,
  integration: integration.value,
  interests: interests.value,
  fields: fields.value,
  lgpd: lgpd.value
});

const setInitialSnapshot = () => {
  initialSnapshot.value = makeSnapshot();
  hasChanges.value = false;
};
watch(
  [assistentStatus, goal, behavior, references, integration, interests, fields, lgpd],
  () => {
    if (!loadedData.value || !initialSnapshot.value) return;
    hasChanges.value = makeSnapshot() !== initialSnapshot.value;
  },
  { deep: true }
);

const handleExitClick = () => {
  if (hasChanges.value) {
    exitPopUp.value = true;
  } else {
    confirmExit();
  }
};

const pageTitle = computed(() => {
    return isEditing.value ? 'Edição do Assistente' : 'Cadastro de novo assistente';
});

const items = computed(() => {
    const tabs = [
        { title: 'Objetivo', value: 'goal', icon: isTabInvalid('goal') },
        { title: 'Comportamento', value: 'behavior', icon: isTabInvalid('behavior') },
        { title: 'Referências', value: 'references', icon: isTabInvalid('references') },
        { title: 'Integração', value: 'integration', icon: isTabInvalid('integration') },
        { title: 'Interesses', value: 'interests', icon: isTabInvalid('interests') },
        { title: 'Campos', value: 'fields', icon: isTabInvalid('fields') },
    ];

    if (lgpd.value.lgpdActive || lgpd.value.lgpdDefinitiveActive) {
        tabs.push({ title: 'LGPD', value: 'lgpd', icon: isTabInvalid('lgpd') || (isEditing.value && !lgpdValid.value) });
    }

    return tabs;
});


const nextTab = () => {
    const index = items.value.findIndex(item => item.value === tab.value);
    if (index < items.value.length - 1) {
        tab.value = items.value[index + 1].value;
    }
};

const previousTab = () => {
    const index = items.value.findIndex(item => item.value === tab.value);
    if (index > 0) {
        tab.value = items.value[index - 1].value;
    }
};

const validateTabs = (currentTab) => {
    const currentIndex = items.value.findIndex(item => item.value === currentTab);
    if (currentIndex === -1) return;
    
    for (let i = 0; i < currentIndex; i++) {
        const tabName = items.value[i].value;
        const form = forms.value[tabName];
        
        if (form?.ref && (!isEditing.value || form.loaded)) {
            form.model = form.ref.validate();
        }
    }
};

const getTabColor = (currentTab) => {
    return !validateForm(currentTab) ? 'alert-system-dark' : 'primary-60';
};

const getTabItemColor = (tabValue) => {
    return !validateForm(tabValue) ? 'alert-system-dark' : 'primary-60';
};

const validateForm = (tabValue) => {
    if (!isCheckTabLoaded(tabValue)) return true;
    if (!forms.value[tabValue]) return true;
    return forms.value[tabValue].model !== false;
};

watch(tab, async (newTab) => {
    if (newTab === 'goal') return;
    validateTabs(newTab);
    if (newTab === 'references' && references.value.uploading_status) {
        await checkUploadingStatus();
    }
}, { immediate: false });

const checkUploadingStatus = async () => {
    try {
        const response = await fetch(`/api/assistents/check-uploading-status?key=${assistentKey}`);
        const data = await response.json();
        
        if (data.success) {
            if (data.uploading !== undefined) {
                references.value.uploading_status = data.uploading;
            }
            if (data.files) {
                references.value.referencesArchives = parseFiles(data.files);
            }
        }
    } catch (err) {
        console.error(err);
    }
}

const inputRules = {
    required: [value => !!value || 'Campo obrigatório.'],
    validUrl: [
        value => {
            if (value && !regexUrl.test(value)) {
                return 'URL inválida. Digite um link válido.';
            }
            return true;
        }
    ],
    maxLength(limit) {
        return [
            value => !!value || 'Campo obrigatório.',
            value => !value || value.length <= limit || `Limite máximo suportado de ${limit} caracteres.`
        ];
    },
    modelInactive(modelStatus) {
        return [
            value => {
                if (!modelStatus && !value) return 'Este modelo de linguagem não está mais disponível.';
                return true;
            }
        ];
    },
    linkDescription: (container) => {
        return !container[0]?.trim() && container[1]?.trim();
    },
    linkUrl: (container) => {
        return container[0]?.trim() && !container[1]?.trim();
    }
}

const loadData = async () => {
    try {
        const [assistentData] = await Promise.all([
            request(`/api/assistents/bot/${assistentKey}`, { error: 'Não foi possível carregar os dados do assistente' }),
            loadRequestData(),
        ]);
        const { status, name, institute_name, color, img_ia, values, specialty,
            checkpoint_status, instructions, chat_ending, presentation, commercial_proposals, model, fallback_model, model_status,
            references_links, references_files, uploading_status, interest_filters, latest_log, course_offers, error_message} = assistentData;

        if (!model_status) tab.value = 'integration'

        assistentStatus.value = status;
        originalAssistentStatus.value = status;
        latestLog.value = latest_log;
        originalImageUrl.value = img_ia || null;

        loadGoal(name, institute_name, color, img_ia, values, specialty);
        loadBehavior(checkpoint_status, instructions, chat_ending, presentation, commercial_proposals, error_message);
        loadReferences(references_links, references_files, uploading_status);
        loadInterests(interest_filters, course_offers);
        loadLgpd(assistentData);

        if (model_status && lgpd.value.lgpdDefinitiveActive && !lgpd.value.lgpdTypeId) tab.value = 'lgpd';

        loadIntegration(assistentData, model, fallback_model, model_status);
        loadFields(assistentData);
        loader.value = false;
    } catch (error) {
        console.error(error);
    } finally {
        integration.value.loading = false;
        fields.value.loading = false;
        loadedData.value = true;
        setInitialSnapshot();
    }
};

const loadGoal = (name, institute_name, color, img_ia, values, specialty) => {
    goal.value = {
        name: name || '',
        institute: institute_name || '',
        color: color || '',
        image: img_ia || null,
        values: values || '',
        speciality: specialty || ''
    };
    checkTabLoaded('goal');
}

const loadBehavior = async (checkpointStatus, instructions, chat_ending, presentation, commercial_proposals, error_message) => {
    behavior.value = {
        instructions: instructions || '',
        endChat: chat_ending || '',
        presentation: presentation || '',
        commercial: commercial_proposals || '',
        checkpointStatus: checkpointStatus || false,
        errorMessage: error_message || ''
    };
    checkTabLoaded('behavior');
}

const loadReferences = (references_links, references_files, uploading_status) => {
    references.value = {
        assistentKey: assistentKey,
        referencesLinks: parseLinks(references_links),
        referencesArchives: parseFiles(references_files),
        uploading_status: uploading_status
    };
    checkTabLoaded('references');
}

const parseLinks = (references_links) => {
    if (!references_links) {
        return
    }
    const arrayLinks = references_links.split(';');
    const mappedArrayLinks = arrayLinks.map(link => {
        link = link.trim();
        const delimiter = link.indexOf(':');
        if (delimiter) {
            const description = link.slice(0, delimiter).trim();
            const url = link.slice(delimiter + 1).trim();
            if (description && url) {
                return [description, url];
            }
        }
        return ['', link];
    }).filter(link => link[0] || link[1]);
    return mappedArrayLinks
}

const parseFiles = (references_files) => {
    if (!references_files) {
        return [];
    }
    return references_files.map(url => {
        const fullPath = url.split('/').pop();
        const filename = fullPath.split('_').slice(1).join('_');
        return {
            name: filename,
            reference: true,
            url: fullPath,
            loader: false,
        };
    });
}

const loadIntegration = (response, model, fallback_model, model_status) => {
    integration.value = {
        ...integration.value,
        loading: false,
        model: model_status ? model : null,
        fallbackModel: fallback_model || null,
        channelSelected: findSelectedChannel(response),
        modelStatus: model_status
    };
    checkTabLoaded('integration');
}

const findSelectedChannel = (response) => {
    if (response && integration.value.channelList) {
        const selected = integration.value.channelList.find((channel) => channel.id === response.origin);
        return selected
    }
    return [];
};

const loadInterests = (interest_filters, courseOffers) => {
    interests.value = {
        ...interests.value,
        conditions: structuredClone(interest_filters) || [],
        saved: structuredClone(interest_filters) || [],
        loading: false,
        courseOffers: courseOffers
    }
    checkTabLoaded('interests');
}

const loadFields = (response) => {
    const savedFields = response.selected_fields || [];
    const savedDefaultIds = new Set(
        savedFields.filter(field => field.is_default).map(field => field.id)
    );
    const savedCustomColumns = new Set(
        savedFields.filter(field => !field.is_default).map(field => field.coluna)
    );
    fields.value.selectedProperties = fields.value.properties.filter(property =>
        property.is_default
            ? savedDefaultIds.has(property.id)
            : savedCustomColumns.has(property.coluna)
    );
    fields.value.loading = false;
    checkTabLoaded('fields');
}

const saveAssistent = async () => {
    try {
        loader.value = true;
        const formData = prepareFormData();
        await request("/api/assistents/bot/save", null, 'POST', formData);

        await toggleStatus();
        snackbar.success(isEditing.value ? 'Assistente atualizado.' : 'Assistente criado.');
    } catch (err) {
        console.error(err);
        snackbar.error('Erro ao salvar assistente');
    } finally {
        loader.value = false;
        router.push('/assistentes');
    }
};

const prepareFormData = () => {
    const links = prepareLinks();
    const { urls, files } = prepareFiles();
    const data = buildAssistantData(links);
    data.push({ name: 'fields', value: JSON.stringify(fields.value.selectedProperties) });
    return createFormData(data, files, urls);
};

const sanitizeContextEntities = (context) => {
  const result = { ...context };
  Object.keys(result).forEach((key) => {
    if (key.startsWith('entidade') && result[key]) {
      const e = result[key];
      result[key] = { id: e.id, nome: e.nome, tipoId: e.tipoId };
    }
  });
  return result;
};

const buildAssistantData = (assistentLinks) => {
  const saved = interests.value?.saved ?? [];

  const idSet = new Set(saved.map(c => String(c.contextoId)));

  const filteredContextData = (contextData.value ?? [])
    .filter(ctx => idSet.has(String(ctx.id)))
    .map(sanitizeContextEntities);

  const savedWithDefinitions = saved.map(item => ({
    ...item,
    definicoes: filteredContextData.filter(def => String(def.id) === String(item.contextoId)),
    }));

  return [
    { name: "key", value: assistentKey || "" },
    { name: "name", value: goal.value.name },
    { name: "institute_name", value: goal.value.institute },
    { name: "color", value: goal.value.color || '#858585' },
    { name: "url_img_ia", value: goal.value.image },
    { name: "specialty", value: goal.value.speciality },
    { name: "original_url_img_ia", value: originalImageUrl.value },
    { name: "values", value: goal.value.values },
    { name: "presentation", value: behavior.value.presentation },
    { name: "instructions", value: behavior.value.instructions },
    { name: "chat_ending", value: behavior.value.endChat },
    { name: "commercial_proposals", value: behavior.value.commercial },
    { name: "model", value: integration.value.model },
    { name: "fallback_model", value: integration.value.fallbackModel },
    { name: "token", value: integration.value.channelSelected?.token },
    { name: "origin", value: integration.value.channelSelected?.id },
    { name: "interest_filters", value: JSON.stringify(savedWithDefinitions) },
    { name: "changed_filters", value: Number(interests.value.changed) },
    { name: "references_links", value: assistentLinks },
    { name: "checkpoint_status", value: Number(behavior.value.checkpointStatus)},
    { name: "error_message", value: behavior.value.errorMessage },
    { name: "course_offers", value: JSON.stringify(interests.value.courseOffers) },
    { name: "lgpd", value: JSON.stringify({
        lgpdTypeId: lgpd.value.lgpdTypeId,
        legalBasisConfig: lgpd.value.legalBasisConfig,
        signaturesConfig: LGPD_TYPES[lgpd.value.lgpdTypeId] === 'LEGITIMATE_INTEREST_CONSENT'
            ? lgpd.value.selectedCrmSignatureIds.map(id => ({ crmSignatureId: id }))
            : lgpd.value.signaturesConfig,
    }) }
  ];
};

const createFormData = (data, files, urls) => {
    const allData = [
        ...data,
        ...files.map(file => ({ name: "files", value: file })),
        ...urls.map(url => ({ name: "references_files", value: `uploads/${url}` }))
    ];

    return allData.reduce((formData, item) => {
        formData.append(item.name, item.value);
        return formData;
    }, new FormData());
};

const prepareLinks = () => {
    if (!references.value.referencesLinks) return ''
    const filterDescWithoutLinks = references.value.referencesLinks.filter(link => link[1]?.trim() || !link[0].trim());
    const arrayOfLinks = filterDescWithoutLinks.map(link => link[0].trim() ? `${link[0]}: ${link[1]}` : link[1]);
    return arrayOfLinks.join(';\n').trim();
}

const prepareFiles = () => {
    const urls = references.value.referencesArchives.filter(archive => archive.reference).map(archive => archive.url);
    const files = references.value.referencesArchives.filter(archive => !archive.reference);
    return { urls, files };
}

const toggleStatus = async () => {
    try {
        if (!(isEditing.value && assistentKey && originalAssistentStatus.value !== assistentStatus.value)) return
        const statusEndpoint = assistentStatus.value ? `/api/assistents/bot/${assistentKey}/enable` : `/api/assistents/bot/${assistentKey}/disable`;
        await fetch(statusEndpoint);
    } catch (err) {
        console.error(err);
    }
}

const loadLgpd = (assistentData) => {
    const setting = assistentData.lgpdSetting;
    if (!setting) return;

    lgpd.value.lgpdTypeId = setting.lgpdTypeId;
    lgpd.value.legalBasisConfig = {
        crmLegalBasisId: setting.crmLegalBasisId,
        privacyText: setting.privacyText,
        acceptanceText: setting.acceptanceText,
        acceptanceKeyword: setting.acceptanceKeyword,
        rejectionKeyword: setting.rejectionKeyword,
    };

    if (assistentData.lgpdSignatures?.length) {
        const type = LGPD_TYPES[setting.lgpdTypeId];
        if (type === 'LEGITIMATE_INTEREST_CONSENT') {
            lgpd.value.selectedCrmSignatureIds = assistentData.lgpdSignatures.map(sig => sig.crm_signature_id);
            return;
        }

    lgpd.value.signaturesConfig = assistentData.lgpdSignatures.map(sig => ({
        crmSignatureId: sig.crm_signature_id,
        acceptanceText: sig.acceptance_text,
        acceptanceKeyword: sig.acceptance_keyword,
        rejectionKeyword: sig.rejection_keyword,
        required: sig.required,
    }));
    }
};

const loadRequestData = async () => {
    try {
        const [channels, properties, models, lgpdOptions] = await Promise.all([
            request('/api/assistents/crm/channels'),
            fetchProperties(),
            request('/api/assistents/listModels'),
            request('/api/assistents/crm/lgpd-options'),
        ]);
        
        integration.value.channelList = channels;
        fields.value.properties = properties;
        integration.value.allModels = models.all_models;
        integration.value.stableModels = models.stable;
        lgpd.value.lgpdActive = lgpdOptions.lgpd_active ?? false;
        lgpd.value.lgpdDefinitiveActive = lgpdOptions.lgpd_definitive_active ?? false;
        lgpd.value.lgpdTypeOptions = lgpdOptions.lgpd_types ?? [];
        lgpd.value.crmSignatureOptions = lgpdOptions.signatures ?? [];
        lgpd.value.crmLegalBasisOptions = lgpdOptions.legal_basis ?? [];
    } catch (err) {
        console.error(err);
    } finally {
        if (!isEditing.value) {
            integration.value.loading = false;
        }
    }
};

onMounted(async () => {
    try {
        if (isEditing.value) {
            loader.value = true
            return loadData();
        }
        loadedData.value = true;
        await loadRequestData();
        setInitialSnapshot();
    } catch (err) {
        console.error(err);
    }
})
</script>

<style lang="scss" scoped>
@use '@/ai_studio/styles/components/assistents-configuration.scss';
.navigation-tab-item {
	font-size: 16px !important;
	color: rgb(var(--v-theme-neutral-35));;
	font-weight: 500;
	line-height: 24px;
}

.buttons-container {
	padding: 16px 32px;
}

.action-buttons-container {
	margin-left: auto;
}

.navigation-buttons {
	position: fixed;
	bottom: 0;
	left: calc(var(--sidebar-width) + 4px);
	right: 0;
	background: white;
	z-index: 2;
	transition: left 0.2s ease;
}

.latest-log-register {
	p {
		color: rgb(var(--v-theme-neutral-35));;
		font-size: 16px;
		font-family: Roboto;
		font-weight: 400;
	}
}

.assistent-configuration-title {
	font-family: Poppins;
	padding: 16px 32px 16px 0;
	h1 {
		font-size: 20px;
		color: #0E1211;
		font-weight: 400;
	}
}
</style>
